import { Card, Avatar, Typography, Button, Grid, Divider } from "@mui/material";
import user from "../../assets/SAP-Dashboard/profile.jpg";
import EnrollDiscoverCard from "./EnrollDiscoverCard";
import { useEffect, useRef, useState } from "react";
import { authFetch } from "../../api";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, SquareArrowOutUpRight } from "lucide-react";
import EventCard from "../pages/dashboard/SAP_Events";

const ProfileCard = ({ sx = {} }) => {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [enrolledProgram, setEnrolledProgram] = useState([]);
  const userId = localStorage.getItem("userId");
  const progress = profileData?.progress_percent || 72;

  // SVG circle setup
  const size = 150;
  const stroke = 8;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (progress / 100) * circumference;

  const scrollRef = useRef(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const cardWidth = 360; // width + margin combined for each card
  const sliderRef = useRef(null);

  const nextCard = () => {
    if (currentIndex < enrolledProgram.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const prevCard = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  // Update slider transform on index change
  useEffect(() => {
    if (sliderRef.current) {
      sliderRef.current.style.transform = `translateX(-${
        currentIndex * cardWidth
      }px)`;
    }
  }, [currentIndex]);

  const fetchProfileData = async () => {
    try {
      if (!userId) {
        console.error("No userId found in localStorage");
        return;
      }

      const response = await authFetch(`profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch profile");
      }

      setProfileData(data.data || null);
    } catch (error) {
      console.error("Error fetching profile:", error.message);
    }
  };

  const fetchEnrolledProgram = async () => {
    try {
      if (!userId) {
        console.error("No userId found in localStorage");
        return;
      }

      const response = await authFetch(`packages/get-enrolled-packages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch profile");
      }

      setEnrolledProgram(data.data || null);
    } catch (error) {
      console.error("Error fetching profile:", error.message);
    }
  };

  useEffect(() => {
    fetchProfileData();
    fetchEnrolledProgram();
  }, []);

  return (
    <Grid container justifyContent="center" alignItems="stretch">
      <Grid item sx={{ flex: 1, width: "100%", maxWidth: 480, mx: "auto" }}>
        <Card
          sx={{
            borderRadius: 4,
            p: 2,
            boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
            textAlign: "center",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            ...sx,
          }}
        >
          <div className="w-full max-w-sm mx-auto p-2 font-inter">
            {/* Switch Profile Button */}
            <button
              onClick={() =>
                navigate(
                  profileData?.switch_path || "/test-prep/testprepdashboard",
                )
              }
              className="w-full bg-[#A41480] hover:bg-[#A41480] text-white rounded-xl text-[13px] md:text-[14px] font-medium py-[18px] transition flex items-center justify-center gap-2 shadow-[0_4px_12px_rgba(164,20,128,0.35)]"
              style={{ height: 33 }}
            >
              <span>
                {profileData?.switch_text || "Switch to Student Test Master"}
              </span>
              <img
                src="/newdashboard/ProfileSection/XMLID_1523_.png"
                alt="icon"
                className="w-4 h-4 object-contain"
              />
            </button>
          </div>

          <div className="">
            {/* Title */}
            <h2 className="text-left font-inter text-[18px] font-semibold text-gray-900 p-4">
              Enrolled Programs
            </h2>

            {/* Carousel Container */}
            {enrolledProgram.length >= 1 ? (
              <div className="relative flex justify-center items-center  px-2 sm:px-4">
                {/* Prev Button – hide if only 1 */}
                {enrolledProgram.length > 1 && currentIndex > 0 && (
                  <button
                    onClick={prevCard}
                    className="absolute left-1 sm:left-2 z-20 bg-white border border-gray-300 
        rounded-full p-1.5 sm:p-2 shadow-md hover:bg-gray-100 
        active:scale-90 transition-transform"
                  >
                    <ChevronLeft className="text-gray-800 w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                )}

                {/* Viewport */}
                <div className="overflow-hidden w-[320px] sm:w-[340px] md:w-[360px]">
                  <div
                    ref={sliderRef}
                    className="flex transition-transform duration-500 ease-in-out"
                  >
                    {enrolledProgram.map((program) => (
                      <div
                        key={program.id}
                        className="w-full flex-shrink-0 flex justify-center"
                      >
                        <div className="relative w-full rounded-2xl border border-[#E6D9F2] bg-gradient-to-t from-purple-50 to-white p-5 shadow-sm">
                          <div className="relative z-10">
                            {/* Header */}
                            <div className="flex justify-between items-start mb-3">
                              <h3 className="text-start font-inter text-[#572575] font-bold text-[16px]">
                                {program.program_name}
                              </h3>
                            </div>

                            {/* Info Section */}
                            <div className="space-y-2 text-sm">
                              <div className="flex items-center gap-3 text-sm text-[#222222] font-inter font-semibold mb-2 mt-2">
                                <p className="flex items-center">
                                  <img
                                    src="/man.png"
                                    alt="man"
                                    className="h-6 w-6"
                                  />
                                  <span className="ml-2 font-inter text-gray-700 font-bold text-[13px]">
                                    {new Date(
                                      program.enrollment_date,
                                    ).toLocaleDateString("en-IN", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })}
                                  </span>
                                </p>

                                {/* {program.duration !== null && ( */}
                                <p className="flex items-center">
                                  <img
                                    src="/clock.png"
                                    alt="clock"
                                    className="h-6 w-6"
                                  />

                                  <span className="ml-2 font-inter text-gray-700 font-bold text-[13px]">
                                    {program.duration} months
                                  </span>
                                </p>
                                {/* )} */}
                              </div>

                              <p className="flex items-center">
                                <img
                                  src="/name.png"
                                  alt="name"
                                  className="h-6 w-6"
                                />
                                <span className="ml-2 font-inter text-gray-700 font-bold text-[13px]">
                                  {program.package_name}
                                </span>
                              </p>
                            </div>

                            {/* Button */}
                            <button
                              onClick={() => navigate("/sap/enrolled-programs")}
                              className="mt-4 w-[50%] flex items-center justify-center gap-2 bg-[#572575] text-white text-sm font-semibold rounded-xl py-1"
                            >
                              <img
                                src="/export.png"
                                alt="export"
                                className="h-3 w-3"
                              />{" "}
                              Explore more
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Next Button – hide if only 1 */}
                {enrolledProgram.length > 1 &&
                  currentIndex < enrolledProgram.length - 1 && (
                    <button
                      onClick={nextCard}
                      className="absolute right-1 sm:right-2 z-20 bg-white border border-gray-300 
        rounded-full p-1.5 sm:p-2 shadow-md hover:bg-gray-100 
        active:scale-90 transition-transform"
                    >
                      <ChevronRight className="text-gray-800 w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  )}
              </div>
            ) : (
              <EnrollDiscoverCard />
            )}
          </div>

          <div className="p-2 mt-4">
            {/* Title */}
            <h2 className="text-left font-inter text-[18px] font-semibold leading-[100%] tracking-[0px] align-middle text-gray-900 p-4">
              Events For You
            </h2>
            <EventCard />
          </div>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ProfileCard;
